import React from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Configuration for toast notifications.
 *
 * @typedef {Object} ToastConfig
 * @property {string} position - Position of the toast notification.
 * @property {number} autoClose - Duration in milliseconds before the toast automatically closes.
 * @property {boolean} closeOnClick - Whether the toast can be closed by clicking on it.
 * @property {boolean} draggable - Whether the toast can be dragged.
 * @property {string} theme - Theme of the toast notification.
 * @property {boolean} pauseOnFocusLoss - Whether to pause the toast on focus loss.
 */

/**
 * Toast configuration settings.
 * @type {ToastConfig}
 */
export const config = {
  position: 'top-right',
  autoClose: 3000,
  closeOnClick: true,
  draggable: true,
  theme: 'colored',
  pauseOnFocusLoss: false,
};

/**
 * Show a toast notification with a specified message and type.
 *
 * @param {string} message - The message to display in the toast.
 * @param {string} [type="success"] - The type of toast notification ("success", "error", "warn", "info").
 */
export const showToast = (message, type = 'success') => {
  toast.dismiss();
  switch (type) {
    case 'success':
      toast.success(message, config);
      break;

    case 'error':
      toast.error(message, config);
      break;

    case 'warn':
      toast.warn(message, config);
      break;

    case 'info':
      toast.info(message, config);
      break;

    default:
      toast.info(message, config);
  }
};

/**
 * ToastMessage component for displaying toast notifications.
 *
 * @returns {JSX.Element} - Rendered ToastMessage component.
 */
const ToastMessage = () => {
  return (
    <ToastContainer
      theme="colored"
      toastStyle={{
        minHeight: '30px',
      }}
      style={{
        fontSize: '1rem',
        width: 'auto',
        minWidth: '200px',
        fontWeight: 700,
        padding: '10px',
      }}
    />
  );
};

export default ToastMessage;
